import DOMUtils from "@utils/dom.utils";
import richTextUtils from "@plugins/richtext/utils/richtext.utils";
import { INTEGRATOR_APP_DROPDOWN_OPTIONS } from "@modules/editor/service/editor.constant";

export default {
  isNumberOfAttachmentValid(content, allowNumber) {
    const container = document.createElement("div");
    container.innerHTML = content;

    const numberOfAttachments = DOMUtils.elOf(container, ".sls-media, .sls-media-object").length;

    if (numberOfAttachments === allowNumber) {
      return true;
    }
    return false;
  },
  isNumberOfAttachmentWithinRange(content, min, max) {
    const container = document.createElement("div");
    container.innerHTML = content;

    const numberOfImageAttachments = DOMUtils.elOf(
      container,
      ".sls-media[media-type='IMAGE'], .sls-media-object[media-type='IMAGE'], .sls-media-tag[media-type='IMAGE']",
    ).length;

    const numberOfAttachments = DOMUtils.elOf(
      container,
      ".google-attachment ,.sls-media:not([media-type='IMAGE']), .sls-media-object:not([media-type='IMAGE']), .sls-media-tag:not([media-type='IMAGE'])",
    ).length;

    const result = {
      isExceed: false,
      isBehind: false,
    };
    // image attachment will count as 1 attachment but will not affect max limit.
    if (numberOfAttachments > max) {
      result.isExceed = true;
    }
    if (numberOfAttachments + numberOfImageAttachments < min) {
      result.isBehind = true;
    }

    result.hasAttachment = numberOfAttachments + numberOfImageAttachments > 0;
    return result;
  },

  /**
   * FE-only wrapper: same shape as `isNumberOfAttachmentWithinRange` but counts images
   * towards the maximum limit. Implemented inline (no helper) so behaviour is isolated.
   * Use this for File Submission flows where images should contribute to max attachment checks.
   *
   * @param content - rich text HTML content
   * @param min - minimum required attachments
   * @param max - maximum allowed attachments
   */
  isNumberOfAttachmentWithinRangeIncludingImages(content, min, max) {
    const container = document.createElement("div");
    container.innerHTML = content;

    // count image attachments (drawings and media objects labelled IMAGE)
    const numberOfImageAttachments = DOMUtils.elOf(
      container,
      ".sls-media[media-type='IMAGE'], .sls-media-object[media-type='IMAGE'], .sls-media-tag[media-type='IMAGE']",
    ).length;

    // count non-image attachments (google attachments and other media types)
    const numberOfNonImageAttachments = DOMUtils.elOf(
      container,
      ".google-attachment, .sls-media:not([media-type='IMAGE']), .sls-media-object:not([media-type='IMAGE']), .sls-media-tag:not([media-type='IMAGE'])",
    ).length;

    const total = numberOfImageAttachments + numberOfNonImageAttachments;

    const result = {
      isExceed: false,
      isBehind: false,
      hasAttachment: false,
    };

    if (total > max) {
      result.isExceed = true;
    }
    if (total < min) {
      result.isBehind = true;
    }

    result.hasAttachment = total > 0;
    return result;
  },

  isAnswerEmpty(content) {
    if (
      content.indexOf("sls-media") > 0 ||
      content.indexOf("sls-external-media") > 0 ||
      content.indexOf("sls-media-object") > 0 ||
      content.indexOf("google-attachment") > 0 ||
      content.indexOf("sls-algebra-kit") > 0 ||
      content.indexOf("sls-frame") > 0
    ) {
      return false;
    }

    const elementContent = DOMUtils.createElement("div");
    elementContent.innerHTML = content;

    const answer = elementContent.textContent.replace(/\n$/, ""); // removes trailing newline
    return answer.trim() === "";
  },
  /**
   * Only get the text content
   * */
  getAnswerText(answer) {
    const container = document.createElement("div");
    container.innerHTML = answer;
    return container.firstChild.textContent;
  },
  dataPatchDirtyRichtextContent(richTextContent) {
    // 1. retrieve white list
    const whiteListJson = sessionStorage.getItem("whiteList");
    if (!whiteListJson) {
      return richTextContent;
    }

    const whiteList = JSON.parse(whiteListJson);
    const supportedDivAttributeList =
      whiteList["div"]["attributes"] + "," + whiteList[":all"]["attributes"];

    // 2. clean up
    const strippedElement = richTextUtils.stripUnsupportedDivAttributes(
      richTextContent,
      supportedDivAttributeList,
    );
    return strippedElement.innerHTML;
  },
  convertIntegratorAppToDropdownOptions(integratorAppAccessByCategory) {
    const dropdownOptions = {
      0: { value: "", label: INTEGRATOR_APP_DROPDOWN_OPTIONS.DEFAULT },
    };
    let dropdownIndex = 1;
    integratorAppAccessByCategory.forEach((integratorApp) => {
      if (integratorApp.accessible && integratorApp.enabled) {
        dropdownOptions[dropdownIndex] = {
          value: integratorApp.appFeatureId.toString(),
          label: integratorApp.appFeatureName,
          key: integratorApp.category,
        };
        dropdownIndex++;
      }
    });
    return dropdownOptions;
  },
  /**
   * FE-only wrapper: extract only ISR(html5/ltiApp) embedded components from rich text HTML.
   * Strips surrounding paragraph/formatting content while preserving supported ISR blocks.
   *
   * @param htmlContent - rich text HTML content
   * @returns extracted ISR embed HTML, or original HTML when no supported embed is found
   */
  extractIsrEmbeddedContent(htmlContent) {
    const container = document.createElement("div");
    container.innerHTML = htmlContent;
    const embedded = container.querySelectorAll(
      ".sls-media-wrapper, .sls-media, .sls-embed-website, .sls-embed-app",
    );
    if (embedded.length === 0) return htmlContent;

    const wrapper = document.createElement("div");
    embedded.forEach((el) => {
      // Avoid duplicates when .sls-media is inside .sls-media-wrapper
      const isNestedInMediaWrapper =
        el.closest(".sls-media-wrapper") && !el.classList.contains("sls-media-wrapper");
      const isNestedInEmbedWebsite =
        el.closest(".sls-embed-website") && !el.classList.contains("sls-embed-website");

      if (isNestedInMediaWrapper || isNestedInEmbedWebsite) return;

      wrapper.appendChild(el.cloneNode(true));
    });

    if (wrapper.childNodes.length === 0) return htmlContent;

    return wrapper.innerHTML;
  },
};
